import { ResizableDiv } from '../../../elements/ResizableDiv'
import { logAmplitudeEvent } from '../../../../utils'
import { LoadingText } from '../../../elements/LoadingText'
import { PreviewableContext } from './previewable-context'
import { useMemo } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import {
  contextSourcesAtom,
  useCurrentWindowToSelection,
  useIsCurrentWindowAttached,
  useSelectedWindows,
  useSetCurrentWindowScope,
  useWindowSelection,
  WindowSelectionType,
} from '../../../../store/atoms'
import { getWordCount } from '../../../../utils/stringUtils'
import { PlusIcon } from '../../../icons'
import { useAddWindow } from '../../../../hooks/useAddWindow'
import { useAttachments } from '../../../../store'
import { useAtom } from 'jotai'
import { attachmentAnimation } from '../../attachments'
import { ExplicitWindowContextScope } from '@highlight/common'
import { twMerge } from 'tailwind-merge'
import { Button } from '@highlight/ui'

export const WindowContext = () => {
  const selectedWindows = useSelectedWindows()
  const { updateSelectedWindow, removeSelectedWindow } = useWindowSelection()
  const currentWindow = useCurrentWindowToSelection()
  const setCurrentWindowScope = useSetCurrentWindowScope()
  const isCurrentWindowAttached = useIsCurrentWindowAttached()
  const addWindow = useAddWindow()
  const attachments = useAttachments()
  const [sources] = useAtom(contextSourcesAtom)

  // Set of app names that have multiple open windows
  const duplicateApps = useMemo(() => {
    const counts = new Map<string, number>()
    for (const w of sources.windows) {
      counts.set(w.appName, (counts.get(w.appName) ?? 0) + 1)
    }
    const dupes = new Set<string>()
    for (const [name, count] of counts) {
      if (count > 1) dupes.add(name)
    }
    return dupes
  }, [sources.windows])

  return (
    <AnimatePresence mode={'popLayout'}>
      {selectedWindows.length === 0 && currentWindow && (isCurrentWindowAttached || !attachments.length) && (
        <motion.div
          // Hook for hosts that suppress the screen-context pill (e.g. the
          // sidepanel's scoped Action View input).
          data-window-context
          key={`attachment-${currentWindow.pid}-${currentWindow.appName}`}
          {...attachmentAnimation}
        >
          <SelectedWindow
            window={currentWindow}
            isActive={isCurrentWindowAttached}
            hasAppDuplicate={duplicateApps.has(currentWindow.appName)}
            onToggleScope={(scope: ExplicitWindowContextScope) => {
              setCurrentWindowScope(scope)
              logAmplitudeEvent('Toggle Window Scope', {
                appName: currentWindow.appName,
                words: getWordCount(
                  (scope === 'visible' ? currentWindow.context?.visibleText : currentWindow.context?.fullText) ?? '',
                ),
                focusedWindow: true,
                scope,
              })
            }}
            onAdd={() => {
              addWindow(currentWindow)
              logAmplitudeEvent('Add Window Context', {
                appName: currentWindow.appName,
                focusedWindow: true,
                trigger: 'Context Bar',
              })
            }}
            onRemove={() => {
              removeSelectedWindow(currentWindow)
              logAmplitudeEvent('Remove Window Context', {
                appName: currentWindow.appName,
                focusedWindow: true,
                trigger: 'Context Bar',
              })
            }}
          />
        </motion.div>
      )}
      {selectedWindows.map((window) => {
        return (
          <motion.div key={`attachment-${window.pid}-${window.appName}-${window.windowTitle}`} {...attachmentAnimation}>
            <SelectedWindow
              key={`window-attachment-${window.pid}-${window.appName}-${window.windowTitle}`}
              isActive={true}
              compact={selectedWindows.length > 1}
              hasAppDuplicate={duplicateApps.has(window.appName)}
              window={window}
              onToggleScope={(scope) => {
                updateSelectedWindow(window, { scope })
                logAmplitudeEvent('Toggle Window Scope', {
                  appName: window.appName,
                  words: getWordCount(
                    (scope === 'visible' ? window.context?.visibleText : window.context?.fullText) ?? '',
                  ),
                  focusedWindow: false,
                  scope,
                })
              }}
              onRemove={() => {
                removeSelectedWindow(window)
                logAmplitudeEvent('Remove Window Context', {
                  appName: window.appName,
                  focusedWindow: false,
                  trigger: 'Context Bar',
                })
              }}
            />
          </motion.div>
        )
      })}
    </AnimatePresence>
  )
}

const SelectedWindow = ({
  isActive = true,
  compact,
  hasAppDuplicate = false,
  window,
  onToggleScope,
  onAdd,
  onRemove,
}: {
  isActive?: boolean
  compact?: boolean
  hasAppDuplicate?: boolean
  window: WindowSelectionType
  onToggleScope: (scope: ExplicitWindowContextScope) => void
  onAdd?: () => void
  onRemove: () => void
}) => {
  const usesBrowserAPI = window.context?.fullTextSource === 'browserAPI'

  const hasAdditionalScope = useMemo(() => {
    if (!window.context) {
      return false
    }
    const context = window.context

    if (context.fullTextSource === 'browserAPI') {
      return true
    }

    if (context?.appName !== window?.appName) {
      return false
    }
    if (!!context?.visibleText?.length && !context?.fullText?.length) {
      return false
    }
    if (!context?.visibleText?.length && !!context?.fullText?.length) {
      return false
    }
    const visibleWordCount = context?.visibleText?.split(' ')?.length ?? 0
    const fullPageWordCount = context?.fullText?.split(' ')?.length ?? 0
    return fullPageWordCount > visibleWordCount
  }, [window])

  const contextTitle = useMemo(() => {
    if (compact && !hasAppDuplicate) {
      return window.appName
    }
    if (!hasAppDuplicate) {
      return window.appName ?? 'Window'
    }
    return (!!window.windowTitle?.length ? window.windowTitle : window.appName) ?? 'Window'
  }, [window, compact, hasAppDuplicate])

  return (
    <PreviewableContext
      preview={
        <SelectableContextPreview
          window={window}
          hasAdditionalScope={hasAdditionalScope}
          usesBrowserAPI={usesBrowserAPI}
          onToggleScope={onToggleScope}
        />
      }
      disabled={!isActive || (!window.context?.displayScreenshot && window.scope === 'browserAPI')}
    >
      <ResizableDiv
        className={twMerge(
          'hover:bg-element hover:text-foreground rounded-lg transition-colors duration-200',
          isActive ? 'cursor-default' : 'cursor-pointer',
        )}
        onClick={(e) => {
          if (isActive) {
            return
          }
          if (typeof onAdd === 'function') {
            e!.stopPropagation()
            onAdd()
          }
        }}
      >
        <div
          className={twMerge(
            // Matches the AttachmentStub pill chrome so window pills read as
            // one family with the other attachment badges.
            'group/pill bg-background shadow-xs relative flex h-7 items-center gap-1.5 rounded-[10px] border border-transparent px-2',
            isActive
              ? 'text-foreground-secondary hover:text-foreground'
              : 'text-foreground-placeholder hover:text-foreground-secondary',
          )}
        >
          <div className={'relative flex'}>
            {window.appIcon && (
              <img
                src={window.appIcon}
                className={twMerge(
                  'size-4 transition-opacity duration-200 group-hover/pill:filter-none',
                  isActive
                    ? 'opacity-100 group-hover/pill:opacity-0'
                    : 'opacity-30 grayscale group-hover/pill:opacity-0',
                )}
              />
            )}
            <div className={'opacity-100 transition-opacity group-hover/pill:opacity-0'}>
              <AnimatedDot
                show={isActive && hasAdditionalScope && !usesBrowserAPI}
                className={'outline-background absolute -bottom-[1px] -right-[1px] outline outline-[2.5px]'}
                selected={window.scope === 'full'}
              />
            </div>

            {/* Close X */}
            <div
              className={twMerge(
                'text-foreground-placeholder hover:text-foreground absolute left-0 top-0 aspect-square h-full opacity-0 transition-[opacity_color]',
                'flex items-center justify-center pb-[2px] pl-[2px]',
                isActive ? 'cursor-pointer group-hover/pill:opacity-100' : 'pointer-events-none',
              )}
              onClick={(e) => {
                e.stopPropagation()
                onRemove()
              }}
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M4.69922 13.8047C4.61328 13.7227 4.55469 13.6211 4.52344 13.5C4.49609 13.3828 4.49805 13.2676 4.5293 13.1543C4.56055 13.0371 4.61719 12.9375 4.69922 12.8555L8.04492 9.50391L4.69922 6.1582C4.61719 6.07617 4.56055 5.97852 4.5293 5.86523C4.50195 5.74805 4.50195 5.63086 4.5293 5.51367C4.56055 5.39648 4.61719 5.29492 4.69922 5.20898C4.78516 5.11914 4.88672 5.06055 5.00391 5.0332C5.12109 5.00586 5.23633 5.00781 5.34961 5.03906C5.4668 5.06641 5.56836 5.12109 5.6543 5.20312L9 8.55469L12.3516 5.20312C12.4766 5.07812 12.6328 5.01562 12.8203 5.01562C13.0078 5.01562 13.168 5.08008 13.3008 5.20898C13.4336 5.33789 13.498 5.49805 13.4941 5.68945C13.4941 5.87695 13.4316 6.0332 13.3066 6.1582L9.95508 9.50391L13.3066 12.8555C13.4316 12.9805 13.4941 13.1367 13.4941 13.3242C13.4941 13.5117 13.4297 13.6719 13.3008 13.8047C13.168 13.9375 13.0078 14.002 12.8203 13.998C12.6328 13.998 12.4766 13.9355 12.3516 13.8105L9 10.459L5.6543 13.8105C5.56836 13.8926 5.4668 13.9473 5.34961 13.9746C5.23633 14.0059 5.12109 14.0059 5.00391 13.9746C4.88672 13.9473 4.78516 13.8906 4.69922 13.8047Z"
                  fill="currentColor"
                />
              </svg>
            </div>

            {/* Add + */}
            {typeof onAdd === 'function' && (
              <div
                className={twMerge(
                  'text-foreground-placeholder hover:text-foreground absolute left-0 top-0 aspect-square h-full opacity-0 transition-[opacity_color]',
                  'flex items-center justify-center pb-[1px] pl-[2px]',
                  !isActive ? 'cursor-pointer group-hover/pill:opacity-100' : 'pointer-events-none',
                )}
              >
                <PlusIcon size={12} />
              </div>
            )}
          </div>
          <LoadingText
            key={`active-context-${window.pid}-${window.appName}`}
            className={twMerge(
              'max-w-48 truncate text-[12px] font-normal',
              window.isLoading ? 'text-foreground-placeholder' : '',
            )}
            isLoading={window.isLoading}
            text={contextTitle}
          />
        </div>
      </ResizableDiv>
    </PreviewableContext>
  )
}

const compactNumber = new Intl.NumberFormat('en', { notation: 'compact', compactDisplay: 'short' })

/** Paragraphs of the page off screen, counting 150 words as one; at least one. */
const countOffscreenParagraphs = (window: WindowSelectionType) => {
  const paragraphs = (text = '') => Math.ceil(text.split(/\s+/).filter(Boolean).length / 150)
  return Math.max(1, paragraphs(window.context?.fullText) - paragraphs(window.context?.visibleText))
}

const describeScope = (window: WindowSelectionType, usesBrowserAPI: boolean) => {
  if (usesBrowserAPI) {
    return window.scope === 'browserAPI' ? 'Using HTML. Attach a screenshot?' : 'Using HTML and the screenshot'
  }
  const count = countOffscreenParagraphs(window)
  const noun = count === 1 ? 'paragraph' : 'paragraphs'
  return `${compactNumber.format(count)} ${noun} ${window.scope === 'visible' ? 'found off screen' : 'in use'}`
}

/**
 * The hover preview of an attached window: its screenshot, and when the window has more than is on
 * screen (or an HTML source), a bar to add or drop that extra context.
 */
const SelectableContextPreview = ({
  window,
  hasAdditionalScope,
  usesBrowserAPI,
  onToggleScope,
}: {
  window: WindowSelectionType
  hasAdditionalScope: boolean
  usesBrowserAPI: boolean
  onToggleScope: (scope: ExplicitWindowContextScope) => void
}) => {
  const validScreenshot = useMemo(() => {
    const screenshot = window.context?.displayScreenshot
    if (!screenshot || typeof screenshot !== 'string') return null

    if (screenshot.startsWith('data:image/') && screenshot.includes('base64,')) {
      return screenshot
    }

    console.warn('Invalid screenshot format for window:', window.windowTitle, 'data:', screenshot?.slice(0, 100))
    return null
  }, [window.context?.displayScreenshot, window.windowTitle])

  const isFull = window.scope === 'full'
  const showScope = hasAdditionalScope && !window.isLoading

  return (
    <div className="flex min-w-72 flex-col">
      {validScreenshot ? (
        <AnimatePresence mode="wait">
          <motion.img
            {...screenshotAppearAnimation}
            key={`screenshot-${window.pid}-${validScreenshot.slice(0, 50)}`}
            src={validScreenshot}
            className="max-h-48 w-full object-contain"
            onError={(e) => {
              console.warn('Failed to load screenshot for window:', window.windowTitle, e)
            }}
          />
        </AnimatePresence>
      ) : null}
      {showScope && (
        <div
          className={twMerge(
            'flex items-center justify-between gap-3 py-2 pl-3 pr-2',
            validScreenshot && 'border-border border-t',
          )}
        >
          <span className="text-foreground-secondary min-w-0 truncate text-sm">
            {describeScope(window, usesBrowserAPI)}
          </span>
          <Button
            size="sm"
            variant={isFull ? 'ghost' : 'secondary'}
            onClick={(e) => {
              // The card closes on any click inside it; this one changes what it shows.
              e.stopPropagation()
              onToggleScope(isFull ? (usesBrowserAPI ? 'browserAPI' : 'visible') : 'full')
            }}
          >
            {isFull ? 'Remove' : 'Add'}
          </Button>
        </div>
      )}
    </div>
  )
}

const AnimatedDot = ({ className, show, selected }: { className?: string; show: boolean; selected: boolean }) => {
  return (
    <AnimatePresence mode={'popLayout'}>
      {show && (
        <motion.div
          {...scopeDotAnimation}
          className={twMerge('bg-yellow y flex h-2 w-2 items-center justify-center rounded-full', className)}
        >
          <AnimatePresence>
            {!selected && (
              <motion.div {...scopeDotInnerAnimation} className={'bg-background h-[4px] w-[4px] rounded-full'} />
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

// Lands at once like the card around it; only the swap to a new screenshot fades out.
const screenshotAppearAnimation = {
  initial: false as const,
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.2 },
}

const toggleScopeAnimation = {
  initial: {
    opacity: 0,
    x: 10,
  },
  animate: {
    opacity: 1,
    x: 0,
  },
  exit: {
    opacity: 0,
    x: 5,
  },
  transition: {
    duration: 0.2,
  },
}

const scopeDotAnimation = {
  initial: {
    opacity: 0,
    x: -5,
  },
  animate: {
    opacity: 1,
    x: 0,
  },
  exit: {
    opacity: 0,
    x: -5,
  },
  transition: {
    type: 'spring',
    stiffness: 300,
    damping: 25,
  },
}

const scopeDotInnerAnimation = {
  initial: { scale: 0 },
  animate: { scale: 1 },
  exit: { scale: 0 },
  transition: { duration: 0.2 },
}
